import React, { useState, useEffect } from 'react';
import { getProducts, addProduct, updateProduct, deleteProduct } from '../services/apiServices';
import styles from './StockManager.module.css';
import { FaEdit, FaSave, FaTimes, FaTrash, FaPlus } from 'react-icons/fa';

const StockManager = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estados para edição
  const [editingProduct, setEditingProduct] = useState(null); // Guarda o produto sendo editado

  // Estados para adicionar novo produto
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newProduct, setNewProduct] = useState({ name: '', price: '', stock: '', preOrderStock: '', category: 'assados' });

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const data = await getProducts();
      const productList = Object.entries(data).map(([name, details]) => ({
        originalName: name, // Guardamos o nome original para a chamada da API
        name,
        ...details
      })).sort((a, b) => a.name.localeCompare(b.name));
      setProducts(productList);
    } catch (e) {
      setError('Falha ao carregar produtos.');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleEdit = (product) => {
    setEditingProduct({ ...product });
  };

  const handleCancelEdit = () => {
    setEditingProduct(null);
  };

  const handleSaveEdit = async () => {
    if (!editingProduct) return;
    try {
      await updateProduct(editingProduct.originalName, {
        newName: editingProduct.name,
        price: editingProduct.price,
        stock: editingProduct.stock,
        preOrderStock: editingProduct.preOrderStock, // Adiciona o estoque de encomenda
        category: editingProduct.category,
      });
      setEditingProduct(null);
      fetchProducts(); // Recarrega a lista
    } catch (e) {
      alert(`Erro ao salvar: ${e.message}`);
      console.error(e);
    }
  };

  const handleDelete = async (productName) => {
    if (window.confirm(`Tem certeza que deseja excluir o produto "${productName}"?`)) {
      try {
        await deleteProduct(productName);
        fetchProducts(); // Recarrega a lista
      } catch (e) {
        alert(`Erro ao excluir: ${e.message}`);
        console.error(e);
      }
    }
  };

  const handleAddNewProduct = async (e) => {
    e.preventDefault();
    if (newProduct.name.length > 20) {
        alert('O nome do produto não pode ter mais de 20 caracteres.');
        return;
    }
    try {
      await addProduct(newProduct);
      setIsAddModalOpen(false);
      setNewProduct({ name: '', price: '', stock: '', category: 'assados' });
      fetchProducts();
    } catch (e) {
      alert(`Erro ao adicionar produto: ${e.message}`);
      console.error(e);
    }
  };

  const renderProductRow = (product) => {
    const isEditing = editingProduct && editingProduct.originalName === product.originalName;

    if (isEditing) {
      return (
        <tr key={product.originalName} className={styles.editingRow}>
          <td><input type="text" value={editingProduct.name} onChange={(e) => setEditingProduct({...editingProduct, name: e.target.value})} maxLength="20" /></td>
          <td><input type="number" step="0.01" value={editingProduct.price} onChange={(e) => setEditingProduct({...editingProduct, price: parseFloat(e.target.value) || 0})} /></td>
          <td><input type="number" value={editingProduct.stock} onChange={(e) => setEditingProduct({...editingProduct, stock: parseInt(e.target.value, 10) || 0})} /></td>
          <td><input type="number" value={editingProduct.preOrderStock} onChange={(e) => setEditingProduct({...editingProduct, preOrderStock: parseInt(e.target.value, 10) || 0})} /></td>
          <td>
            <select value={editingProduct.category} onChange={(e) => setEditingProduct({...editingProduct, category: e.target.value})}>
              <option value="assados">Assados</option>
              <option value="acompanhamentos">Acompanhamentos</option>
              <option value="bebidas">Bebidas</option>
            </select>
          </td>
          <td className={styles.actions}>
            <button onClick={handleSaveEdit} className={styles.saveBtn}><FaSave /> Salvar</button>
            <button onClick={handleCancelEdit} className={styles.cancelBtn}><FaTimes /> Cancelar</button>
          </td>
        </tr>
      );
    }

    return (
      <tr key={product.originalName}>
        <td>{product.name}</td>
        <td>R$ {product.price.toFixed(2)}</td>
        <td>{product.stock ?? 0}</td>
        <td>{product.preOrderStock ?? 0}</td>
        <td>{product.category}</td>
        <td className={styles.actions}>
          <button onClick={() => handleEdit(product)}><FaEdit /> Editar</button>
          <button onClick={() => handleDelete(product.originalName)} className={styles.deleteBtn}><FaTrash /> Excluir</button>
        </td>
      </tr>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Gerenciamento de Estoque e Produtos</h2>
        <button onClick={() => setIsAddModalOpen(true)} className={styles.addProductBtn}><FaPlus /> Adicionar Produto</button>
      </div>

      {loading && <p>Carregando produtos...</p>}
      {error && <p className={styles.error}>{error}</p>}

      {!loading && !error && (
        <div className={styles.tableWrapper}>
          <table className={styles.productsTable}>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Preço</th>
                <th>Estoque (Dia)</th>
                <th>Estoque (Encomenda)</th>
                <th>Categoria</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {products.map(renderProductRow)}
            </tbody>
          </table>
        </div>
      )}

      {isAddModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h2>Adicionar Novo Produto</h2>
            <form onSubmit={handleAddNewProduct}>
              <input type="text" placeholder="Nome do Produto (máx 20 caracteres)" value={newProduct.name} onChange={(e) => setNewProduct({...newProduct, name: e.target.value})} required maxLength="20" />
              <input type="number" placeholder="Preço (ex: 45.50)" value={newProduct.price} onChange={(e) => setNewProduct({...newProduct, price: e.target.value})} required step="0.01" />
              <input type="number" placeholder="Estoque do Dia" value={newProduct.stock} onChange={(e) => setNewProduct({...newProduct, stock: e.target.value})} required />
              <input type="number" placeholder="Estoque de Encomenda" value={newProduct.preOrderStock} onChange={(e) => setNewProduct({...newProduct, preOrderStock: e.target.value})} required />
              <select value={newProduct.category} onChange={(e) => setNewProduct({...newProduct, category: e.target.value})}>
                <option value="assados">Assados</option>
                <option value="acompanhamentos">Acompanhamentos</option>
                <option value="bebidas">Bebidas</option>
              </select>
              <div className={styles.modalActions}>
                <button type="button" onClick={() => setIsAddModalOpen(false)}>Cancelar</button>
                <button type="submit">Adicionar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockManager;